<!--
 * @Author: YuanYuan
 * @Date: 2024-02-06 10:18:01
 * @LastEditTime: 2026-04-10 17:20:58
 * @description: 带快捷键的时间范围选择器
-->
<template>
  <el-date-picker
    class="w-full"
    :type="type"
    :start-placeholder="startPlaceholder || (type === 'daterange' ? $t('common.startDate') : $t('common.startTime'))"
    :end-placeholder="endPlaceholder || (type === 'daterange' ? $t('common.endDate') : $t('common.endTime'))"
    range-separator="-"
    :value-format="type === 'daterange' ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm:ss'"
    :clearable="clearable"
    :picker-options="!noPicker && localPickerOptions"
    v-model="localValue"
    :title="localValue ? localValue.join(' - ') : ''"
    :default-time="['00:00:00', '23:59:59']">
    <div slot="clear-btn" v-if="!clearable"></div>
  </el-date-picker>
</template>

<script setup>
import { toRefs, computed, getCurrentInstance } from 'vue'
import dayjs from 'dayjs'
import { timeStr2TimeType, timeShortcutsFormatter } from '@/utils/time'

const $vm = getCurrentInstance().proxy

const props = defineProps({
  value: {
    type: Array,
    default: () => [],
    required: true
  },
  type: {
    type: String,
    default: 'datetimerange',
    validator: value => ['datetimerange', 'daterange'].includes(value)
  },
  clearable: {
    type: Boolean,
    default: false
  },
  noPicker: {
    type: Boolean,
    default: false
  },
  pickerOptions: {
    type: Object,
    default: () => ({})
  },
  // 可查询开始时间, 近一年 1y
  queryableStart: {
    type: String,
    default: ''
  },
  // 查询时间不超过 xx 天
  exceedRange: {
    type: Number,
    default: 0
  },
  startPlaceholder: {
    type: String,
    default: ''
  },
  endPlaceholder: {
    type: String,
    default: ''
  }
})

const { type, pickerOptions, queryableStart, exceedRange, value, noPicker, startPlaceholder, endPlaceholder } = toRefs(props)
const emit = defineEmits(['input'])

const formatType = computed(() => {
  return type.value === 'daterange' ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss'
})

const getTimeSpan = (arr, type = 'days') => {
  return dayjs(arr[1]).diff(dayjs(arr[0]), type) + 1
}

const emitTimeRange = (arr = []) => {
  if (arr && arr.length) {
    if (exceedRange.value && getTimeSpan(arr) > exceedRange.value) {
      // 时间间隔不超过 exceedRange 天
      localValue.value = value.value || []
      $vm.$message.warning($vm.$t('common.cannotExceedDays', { d: exceedRange.value }))
    } else {
      emit('input', arr)
    }
  } else {
    emit('input', [])
  }
}

const localValue = computed({
  get() {
    return value.value
  },
  set(n) {
    if (n && n.length === 2) {
      const start = dayjs(n[0]).format(formatType.value)
      const end = dayjs(n[1]).format(formatType.value)
      emitTimeRange([start, end])
    } else {
      emitTimeRange([])
    }
  }
})

const defaultPickerOptions =  {
  shortcuts: timeShortcutsFormatter([
    { text: $vm.$t('common.lastHours', { h: 6 }), value: '-6h' },
    { text: $vm.$t('common.today'), value: '0d', extra: ['day', 'day'] },
    { text: $vm.$t('common.yesterday'), value: ['-1d', '-1d'], extra: ['day', 'day'] },
    { text: $vm.$t('common.lastDays', { d: 7 }), value: '-7d' },
    { text: $vm.$t('common.lastDays', { d: 30 }), value: '-30d' },
  ], formatType.value),
  disabledDate: date => {
    const current = dayjs()
    if (queryableStart.value) {
      const { num, type } = timeStr2TimeType(queryableStart.value)
      return dayjs(date).isAfter(current, 'd') ||
            dayjs(date).isBefore(current.subtract(num, type).format(formatType.value), 'd')
    } else {
      return dayjs(date).isAfter(current, 'd')
    }
  }
}

const localPickerOptions = computed(() => ({ ...defaultPickerOptions, ...pickerOptions.value }))
</script>
